<template>
  <div class="product-view default-page">
    <div class="row">
      <div class="col-lg-6 product-view__swiper">
        <swiper
          :slides-per-view="1"
          :space-between="30"
          :speed="500"
          :loop="true"
          :autoplay="{
            delay: 5000,
            disableOnInteraction: false,
          }"
          :pagination="{
            el: '.custom-swiper-pagination',
            clickable: true,
          }"
          :modules="modules"
        >
          <swiper-slide v-for="image in product?.images" :key="image.id">
            <div class="slide-item">
              <img
                :src="image?.url"
                :alt="product?.name"
                class="slide-item__img"
              />
              <div v-if="product?.is_sale" class="slide-item__sale">
                <span v-if="product?.badge_text">{{
                  product?.badge_text
                }}</span>
                <span v-else>Ціну знижено!</span>
              </div>
            </div>
          </swiper-slide>
          <div class="custom-swiper-pagination mt-3"></div>
        </swiper>
      </div>
      <div class="col-lg-6 product-view__info">
        <h1>{{ product?.name }}</h1>
        <div v-if="!isFullDescription" class="product-view__description">
          <div class="mb-3">
            {{ description }}
          </div>
          <div
            @click="isFullDescription = true"
            class="description-read-more"
            role="button"
          >
            Читати повністю
          </div>
        </div>
        <div v-else class="product-view__description">
          <div class="mb-3">{{ fullDescription }}</div>
          <div
            @click="isFullDescription = false"
            class="description-read-more"
            role="button"
          >
            Сховати
          </div>
        </div>
        <div class="product-view__order">
          <div class="product-view__price">
            <div v-if="product?.is_available">
              <div
                v-if="product.old_price"
                class="product-view__price-old d-inline-block"
              >
                {{ product.old_price }}
                <span class="product-view__price-old-grn">грн</span>
              </div>
              <div :class="{ 'product-view__price-red': product.old_price }">
                {{ product.price }}
                <span
                  class="product-view__price-grn"
                  :class="{ 'product-view__price-red-grn': product.old_price }"
                >
                  грн
                </span>
              </div>
            </div>
            <div v-else class="text-muted">Очікується</div>
          </div>
          <router-link :to="{ name: 'checkout' }">
            <app-btn class="btn btn-primary">Замовити</app-btn>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { mutationTypes } from "@/store";
import { mutationTypes as shopMutationTypes } from "@/store/modules/shop";
import ProductService from "@/services/ProductService";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay, Pagination } from "swiper/modules";
import StringMixin from "@/mixins/string";
import AppBtn from "@/components/common/ui/AppBtn.vue";

export default {
  name: "ProductView",
  components: { AppBtn, SwiperSlide, Swiper },
  setup() {
    const { stripTags, stringLimit } = StringMixin.methods;
    const store = useStore();
    const route = useRoute();
    const Product = new ProductService();
    const productId = ref(route.params.id);
    const isFullDescription = ref(false);
    const modules = ref([Autoplay, Pagination]);

    const setSpinner = (payload) =>
      store.commit(mutationTypes.SET_SPINNER, payload);

    const description = computed(() => {
      return stringLimit(stripTags(product.value?.description), 100);
    });

    const fullDescription = computed(() => {
      return stripTags(product.value?.description);
    });

    const product = computed({
      get: () => store.getters["shop/product"],
      set: (payload) =>
        store.commit(`shop/${shopMutationTypes.SET_PRODUCT}`, payload),
    });

    const getProductData = async () => {
      setSpinner(true);
      try {
        const productRes = await Product.getProduct(productId.value);

        if (productRes.status === 200) {
          product.value = productRes.data;
        } else {
          throw productRes;
        }
      } catch (e) {
        console.error(
          "❌ Проблема з завантаженням продуктів або категорій:",
          e
        );
      } finally {
        setSpinner(false);
      }
    };

    onMounted(() => {
      getProductData();
    });

    return {
      product,
      isFullDescription,
      fullDescription,
      description,
      modules,
    };
  },
};
</script>

<style scoped lang="less">
@import "@/styles/_variables.less";
.product-view {
  &__swiper {
    margin-bottom: 35px;

    .slide-item {
      position: relative;
      display: flex;
      justify-content: center;
      background-color: @white;
      aspect-ratio: 350/180;
      max-width: 100%;
      border-radius: 15px;
      overflow: hidden;

      &__img {
        height: 100%;
        aspect-ratio: 1/1;
        object-fit: cover;
      }

      &__sale {
        position: absolute;
        top: 0;
        left: 0;
        border-radius: 15px 0;
        background: linear-gradient(109deg, #ffd159 20.34%, #ff2b2b 62.78%);
        color: #fff;
        padding: 6px 13px;
        font-size: 12px;
        font-weight: 500;
      }
    }
  }

  &__description {
    margin-bottom: 35px;
    .description-read-more {
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 21px;
      color: #427fff;
    }
  }

  &__order {
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (min-width: 320px) {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: end;
    }

    .btn {
      width: 100%;
      padding-right: 45px;
      padding-left: 45px;
      margin-bottom: 15px;

      @media (min-width: 320px) {
        width: auto;
      }
    }
  }

  &__price {
    left: 15px;
    font-style: normal;
    font-weight: 700;
    font-size: 32px;
    line-height: 32px;
    color: @white;
    margin-bottom: 15px;

    &-grn {
      font-style: normal;
      font-weight: 700;
      font-size: 20px;
      line-height: 24px;
      letter-spacing: 0.005em;
      color: @white;
    }

    &-red {
      color: @red;

      &-grn {
        color: @red;
      }
    }

    &-old {
      font-style: normal;
      font-weight: 400;
      font-size: 24px;
      line-height: 28px;
      color: @gray-light2;
      position: relative;
      margin-bottom: 7px;

      &:after {
        content: "";
        position: absolute;
        left: 0;
        bottom: 12px;
        width: 100%;
        height: 1px;
        background: @gray-light2;
      }

      &-grn {
        font-style: normal;
        font-weight: 700;
        font-size: 18px;
        line-height: 18px;
        color: @gray-light2;
      }
    }
  }
}
</style>
